// src/pages/LoginPage.jsx

import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../services/supabase/supabase-config';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

// Fluxo de login do plugin do AutoCAD (padrao OAuth loopback):
//
//   1. O plugin abre rnplugin.com.br/login?from=plugin no navegador
//   2. E sobe um servidor local ouvindo em localhost:52857/callback/
//   3. Apos o login, ESTA pagina redireciona de volta para esse endereco,
//      levando os tokens na query string
//   4. O plugin le os tokens, fecha a aba e efetiva a sessao
//
// A porta e o caminho sao fixos e casam com o HttpListener do plugin em
// LoginViewModel.StartListenerAndOpenBrowserAsync. Se um mudar, o outro tem que
// mudar junto — nao ha negociacao entre os dois.
const PLUGIN_CALLBACK = 'http://localhost:52857/callback/';

const montarCallbackPlugin = (session) =>
  `${PLUGIN_CALLBACK}?token=${encodeURIComponent(session.access_token)}` +
  `&refresh=${encodeURIComponent(session.refresh_token)}`;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isFromPlugin =
    new URLSearchParams(location.search).get('from') === 'plugin';

  // Guarda o link do callback quando ele fica pronto. Serve de fallback visivel:
  // se o redirect automatico para localhost nao acontecer — listener ainda nao
  // pronto, navegador bloqueando a navegacao http — o usuario ve um botao em vez
  // de uma tela travada.
  const [callbackPlugin, setCallbackPlugin] = useState(null);

  // UM UNICO ponto de saida, para nao repetir o bug do redirect duplo. Tudo passa
  // por este effect, que reage a `user`: vale tanto para quem ja estava logado
  // quanto para quem acabou de logar (o login muda `user` e o effect roda de
  // novo). O handleSubmit apenas autentica; nao redireciona. Assim nunca ha dois
  // caminhos competindo, que era o que travava o login manual em localhost.
  const jaDevolveu = useRef(false);

  React.useEffect(() => {
    if (!user) return;
    if (!isFromPlugin) {
      navigate('/dashboard');
      return;
    }
    if (jaDevolveu.current) return;
    jaDevolveu.current = true;
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) {
        const url = montarCallbackPlugin(data.session);
        setCallbackPlugin(url);
        window.location.href = url;
      }
    });
  }, [user, isFromPlugin, navigate]);

  // Quando ja ha sessao e o fluxo e do plugin, nao mostramos o formulario: a tela
  // vira "voltando ao AutoCAD", com o botao manual caso o redirect nao dispare.
  if (isFromPlugin && user) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Autorizando o plugin</title>
        </Helmet>
        <div className="flex items-center justify-center min-h-screen bg-rn-bg px-4">
          <Card className="w-full max-w-md p-8 text-center" hover={false}>
            <h2 className="text-2xl font-semibold mb-4">Voltando ao AutoCAD…</h2>
            <p className="text-rn-text-soft mb-6">
              Você já está conectado. Estamos devolvendo a autorização para o
              plugin.
            </p>
            {callbackPlugin && (
              <a href={callbackPlugin}>
                <Button className="w-full">
                  Não voltou? Clique para autorizar o plugin
                </Button>
              </a>
            )}
          </Card>
        </div>
      </>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setLoading(true);

    if (!email || !password) {
      setErrorMessage('Por favor, preencha todos os campos.');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setErrorMessage(error.message);
      }
      // Sucesso nao redireciona aqui: autenticar muda `user`, e o useEffect
      // acima leva ao destino certo (plugin ou dashboard). Um caminho so.
    } catch (error) {
      setErrorMessage(
        'Ocorreu um erro desconhecido. Tente novamente mais tarde.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Login</title>
      </Helmet>

      <div className="flex items-center justify-center min-h-screen bg-rn-bg px-4">
        <Card className="w-full max-w-md p-8" hover={false}>
          <h2 className="text-2xl font-semibold text-center mb-6">Login</h2>

          {isFromPlugin && (
            <p className="mb-6 text-sm text-center text-blue-800 bg-blue-50 border border-blue-100 rounded-md py-2 px-3">
              Entre para autorizar o plugin no AutoCAD. Ao final, você volta
              automaticamente para o programa.
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-rn-navy">
                E-mail
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-rn-line rounded-md shadow-sm placeholder-rn-text-soft focus:outline-none focus:ring-2 focus:ring-rn-orange-text focus:border-rn-orange-text"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-rn-navy">
                Senha
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-rn-line rounded-md shadow-sm placeholder-rn-text-soft focus:outline-none focus:ring-2 focus:ring-rn-orange-text focus:border-rn-orange-text"
              />
            </div>

            {errorMessage && (
              <p className="text-sm text-red-600">{errorMessage}</p>
            )}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <Link to="/forgot-password" className="font-medium text-rn-orange-text hover:text-rn-orange">
              Esqueceu a senha?
            </Link>
          </div>
          <div className="mt-2 text-center text-sm">
            <p className="text-rn-text-soft">
              Não tem uma conta?{' '}
              <Link
                to={isFromPlugin ? '/signup?from=plugin' : '/signup'}
                className="font-medium text-rn-orange-text hover:text-rn-orange"
              >
                Cadastre-se
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </>
  );
};

export default LoginPage;
